import React from "react";
import { Button, Grid, TextField } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useAuth } from "hooks";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppSwitch from "components/Shared/AppSwitch";
import { useLazyGetDDLSendOutQuery } from "store/services/sendOutApi";

const INITIAL_STATE = {
  account: "",
  entity: "",
  shipmentId: [],
  sendOutId: "",
  description: "",
  isActive: true,
};

const SendOutCollectionForm = ({
  isBusy = false,
  collectionDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchSendOut = useWatch({ control, name: "sendOut" });

  const unloadedShipments = (_watchSendOut?.shipmentrequest || []).filter(
    (shipment) => shipment.status === "SendOut" && shipment.isOnHold !== true
  );

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const [fetchSendOuts, { data: sentoutsRaw, isLoading: loadingSentOuts }] =
    useLazyGetDDLSendOutQuery();

  const sendOutsArray = sentoutsRaw ? Object.values(sentoutsRaw) : [];
  const availableSendOuts = sendOutsArray.filter(
    (sendOut) =>
      sendOut.shipmentrequest.some(
        (shipment) =>
          shipment.status === "SendOut" && shipment.isOnHold !== true
      ) || collectionDetail?.sendOutId?._id === sendOut._id
  );
  const handleOnSubmit = (data) => {
    const payload = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      shipmentId: data?.shipmentId?.map((item) => item?._id),
      sendOutId: data?.sendOut?._id,
    };
    onSubmit(payload);
  };

  React.useEffect(() => {
    if (collectionDetail) {
      reset({
        ...collectionDetail,
        account: collectionDetail.account,
        entity: collectionDetail.entity,
        shipmentId: collectionDetail.shipmentId || [],
        sendOut: collectionDetail?.sendOutId,
      });
    }
  }, [collectionDetail, reset]);

  React.useEffect(() => {
    if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id) {
      fetchEntities({ account: _watchAccount._id });
    }
  }, [_watchAccount, fetchEntities, isAccountAdmin, isSystemAdmin]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchSendOuts({
      account: _watchAccount?._id,
      entity: _watchEntity._id,
      timestamp: new Date().getTime(),
    });
  }, [_watchAccount?._id, _watchEntity?._id, fetchSendOuts]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Account is required" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, value) => {
                      field.onChange(value);
                      setValue("entity", "");
                      setValue("shipmentId", []);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    disabled={isAccountAdmin}
                    loadingText="Loading Accounts..."
                    error={!!errors?.account}
                    helperText={errors?.account?.message}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Entity is required" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Entity"
                    required
                    value={field.value ?? ""}
                    onChange={(_, value) => {
                      field.onChange(value);
                      setValue("shipmentId", []);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    error={!!errors?.entity}
                    helperText={errors?.entity?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Controller
              name="sendOut"
              control={control}
              rules={{ required: "Please select a Send Out." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Send Out "
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("shipmentId", []);
                  }}
                  getOptionLabel={(option) => option?.sendOutId ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={availableSendOuts || []}
                  loading={loadingSentOuts}
                  loadingText="Loading send out ..."
                  helperText={errors?.sendOut?.message}
                  error={!!errors?.sendOut}
                  disabled={!!collectionDetail}
                />
              )}
            />
          </Grid>

          {/* Shipment ID */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select at least one Shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Shipment"
                  required
                  multiple
                  value={value ?? []}
                  onChange={(_, data) => {
                    onChange(data);
                  }}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={unloadedShipments}
                  loading={loadingSentOuts}
                  loadingText="Loading shipment IDs..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                  disabled={!_watchSendOut}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                />
              )}
            />
          </Grid>

          {collectionDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {collectionDetail ? "Update Collection" : "Add Collection"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default SendOutCollectionForm;
